
const parseMonthDay = (dateString) => {
  if (!dateString) return null;

  const normalized = String(dateString).split('T')[0];
  const parts = normalized.split('-');
  if (parts.length !== 3) return null;

  const month = Number(parts[1]);
  const day = Number(parts[2]);
  if (!month || !day) return null;

  return { month, day };
};

const buildLocalDate = (year, month, day) => {
  const date = new Date(year, month - 1, day);
  if (
    date.getFullYear() !== year ||
    date.getMonth() !== month - 1 ||
    date.getDate() !== day
  ) {
    return null;
  }

  return date;
};

const toIsoDate = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

export const resolveActiveFiscalYear = (storedFiscalYear, referenceDate = new Date()) => {
  if (!storedFiscalYear?.start || !storedFiscalYear?.end) return storedFiscalYear;

  const startMonthDay = parseMonthDay(storedFiscalYear.start);
  const endMonthDay = parseMonthDay(storedFiscalYear.end);
  if (!startMonthDay || !endMonthDay) return storedFiscalYear;

  const startsLaterInYear =
    startMonthDay.month > endMonthDay.month ||
    (startMonthDay.month === endMonthDay.month && startMonthDay.day > endMonthDay.day);

  let startYear = referenceDate.getFullYear();
  let endYear = referenceDate.getFullYear();

  if (startsLaterInYear) {
    const todayMonth = referenceDate.getMonth() + 1;
    const todayDay = referenceDate.getDate();
    const hasReachedStart =
      todayMonth > startMonthDay.month ||
      (todayMonth === startMonthDay.month && todayDay >= startMonthDay.day);

    startYear = hasReachedStart ? referenceDate.getFullYear() : referenceDate.getFullYear() - 1;
    endYear = startYear + 1;
  }

  const resolvedStart = buildLocalDate(startYear, startMonthDay.month, startMonthDay.day);
  const resolvedEnd = buildLocalDate(endYear, endMonthDay.month, endMonthDay.day);
  if (!resolvedStart || !resolvedEnd) return storedFiscalYear;

  return {
    start: toIsoDate(resolvedStart),
    end: toIsoDate(resolvedEnd)
  };
};

export const isWithinFiscalYear = (dateString, fiscalYear) => {
  if (!dateString || !fiscalYear?.start || !fiscalYear?.end) return false;

  const resolvedFiscalYear = resolveActiveFiscalYear(fiscalYear);
  
  // Parse as local date to avoid timezone issues
  // DHIS2 dates like '2026-04-01T00:00:00.000' should be treated as local midnight
  const dateStr = dateString.split('T')[0]; // Extract just the date part (YYYY-MM-DD)
  const startStr = resolvedFiscalYear.start.split('T')[0];
  const endStr = resolvedFiscalYear.end.split('T')[0];
  
  const date = new Date(dateStr + 'T00:00:00');
  const start = new Date(startStr + 'T00:00:00');
  const end = new Date(endStr + 'T00:00:00');
  
  // Include end date by adding 1 day for comparison
  const endWithBuffer = new Date(end);
  endWithBuffer.setDate(endWithBuffer.getDate() + 1);
  
  return date >= start && date < endWithBuffer;
};

export const isFiscalYearActive = (fiscalYear) => {
  if (!fiscalYear?.start || !fiscalYear?.end) return false;

  const resolvedFiscalYear = resolveActiveFiscalYear(fiscalYear);
  
  const today = new Date();
  const start = new Date(resolvedFiscalYear.start);
  const end = new Date(resolvedFiscalYear.end);
  const endWithBuffer = new Date(end);
  endWithBuffer.setDate(endWithBuffer.getDate() + 1);
  
  return today >= start && today <= endWithBuffer;
};

export const getFiscalYearLabel = (fiscalYear) => {
    if (!fiscalYear?.start || !fiscalYear?.end) return 'Unknown Period';

    const resolvedFiscalYear = resolveActiveFiscalYear(fiscalYear);

    const startYear = new Date(resolvedFiscalYear.start).getFullYear();
    const endYear = new Date(resolvedFiscalYear.end).getFullYear();

    return startYear === endYear
        ? `FY ${startYear}`
        : `FY ${startYear}/${endYear}`;
};

export const getFallbackFiscalYear = () => {
    const now = new Date();
    const year = now.getFullYear();
    return { start: `${year}-01-01`, end: `${year}-12-31` };
};

export const isFiscalYearConfigValid = (fiscalYear) => {
    if (!fiscalYear?.start || !fiscalYear?.end) return false;

    const now = new Date();
    const start = new Date(fiscalYear.start);
    const end = new Date(fiscalYear.end);

    // Check if dates are valid
    if (isNaN(start.getTime()) || isNaN(end.getTime())) return false;

    // The fiscal year start should be within ±2 years of today (allow some flexibility)
    const yearDiff = Math.abs(start.getFullYear() - now.getFullYear());
    return yearDiff <= 2;
};

export const getValidatedFiscalYear = (storedCalendar) => {
    if (!storedCalendar?.financialYearStart || !storedCalendar?.financialYearEnd) {
        return getFallbackFiscalYear();
    }

    const resolved = resolveActiveFiscalYear({
        start: storedCalendar.financialYearStart,
        end: storedCalendar.financialYearEnd,
    });

    // Validate the resolved fiscal year is reasonable
    if (!isFiscalYearConfigValid(resolved)) {
        console.warn('⚠️ [FISCAL YEAR] Stored fiscal year is invalid or too old, falling back to current calendar year');
        return getFallbackFiscalYear();
    }

    return resolved;
};

